/**
 * Reusable 4-card stat row matching the Dashboard / Insights aesthetic.
 * Pages pass an array of cards; this component renders the same dark-zinc
 * card grid with consistent loading + error states.
 *
 * Cards are presentation-only — every value comes from the API via the
 * caller. Skeleton state is shown when `loading` is true.
 */
import type { ReactNode } from "react";

export interface SummaryCard {
  label: string;
  value: string;
  hint?: string;
  /** Optional trailing element rendered alongside the label (e.g. status dot). */
  trailing?: ReactNode;
}

interface Props {
  cards: SummaryCard[];
  /** Show skeleton placeholders while data is loading. */
  loading?: boolean;
  /** Error message — when set, renders an inline error in the row. */
  error?: string | null;
}

const SKELETON_COUNT = 4;

export function SummaryCards({ cards, loading = false, error = null }: Props) {
  if (error) {
    return (
      <p
        role="alert"
        className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
      >
        {error}
      </p>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <div
            key={i}
            aria-hidden="true"
            className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
          >
            <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="mt-3 h-7 w-16 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="mt-2 h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-zinc-200 bg-white p-4 shadow-card-light transition hover:border-zinc-300 dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02] dark:hover:border-zinc-700/80 dark:hover:bg-zinc-900/60"
        >
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
              {c.label}
            </div>
            {c.trailing}
          </div>
          <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {c.value}
          </div>
          {c.hint && (
            <div className="mt-1 text-[11.5px] text-zinc-500 dark:text-zinc-500">{c.hint}</div>
          )}
        </div>
      ))}
    </div>
  );
}
